import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Button } from '../../components/Button';
import { TextField } from '../../components/Field';
import { Icon } from '../../components/Icon';
import { Paper } from '../../components/Paper';
import { Turnstile, turnstileEnabled } from '../../components/Turnstile';
import { supabase } from '../../lib/supabase';
import styles from '../../routes/Welcome.module.css';

const showGoogle = import.meta.env.VITE_AUTH_GOOGLE === 'true';
const showApple = import.meta.env.VITE_AUTH_APPLE === 'true';

/** Email magic-link sign-in (joining and returning use the same form). */
export function SignInCard({ className }: { className?: string }) {
  const [email, setEmail] = useState('');
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);

  const redirectTo = `${window.location.origin}/auth/callback`;

  const sendLink = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setStatus('sending');
    const { error: authError } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: redirectTo, captchaToken: captcha ?? undefined },
    });
    if (authError) {
      setStatus('idle');
      setCaptchaKey((k) => k + 1);
      setError(
        authError.status === 429
          ? 'Too many sign-in emails were requested. Wait a few minutes and try again.'
          : "We couldn't send a sign-in link to that address. Check it and try again.",
      );
      return;
    }
    setStatus('sent');
  };

  const oauth = (provider: 'google' | 'apple') => supabase.auth.signInWithOAuth({ provider, options: { redirectTo } });

  return (
    <Paper className={className} id="join">
      {status === 'sent' ? (
        <div className={styles.sent} role="status">
          <h2 className={styles.formTitle}>Check your email</h2>
          <p>
            We sent a sign-in link to <strong>{email}</strong>. Tap the button in that email to continue.
          </p>
          <p>
            It comes from Roadrunner (hello@roadrunner.town). Not there in a minute or two? Check your Spam or Junk
            folder, and mark it “Not spam” so our emails reach your inbox next time.
          </p>
          <Button variant="quiet" onClick={() => setStatus('idle')}>
            Use a different email
          </Button>
        </div>
      ) : (
        <form className={styles.form} onSubmit={sendLink}>
          <div>
            <h2 className={styles.formTitle}>Join your neighbors</h2>
            <p className={styles.formSub}>Free. Already a member? Use the same form to sign in.</p>
          </div>
          {(showGoogle || showApple) && (
            <>
              <div className={styles.oauth}>
                {showApple && (
                  <Button block onClick={() => oauth('apple')}>
                    Continue with Apple
                  </Button>
                )}
                {showGoogle && (
                  <Button block onClick={() => oauth('google')}>
                    Continue with Google
                  </Button>
                )}
              </div>
              <div className={styles.divider}>or use email</div>
            </>
          )}
          <TextField
            label="Email address"
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={error}
            hint="No password. We'll email you a link to sign in."
          />
          {turnstileEnabled && <Turnstile onToken={setCaptcha} resetKey={captchaKey} />}
          <Button
            type="submit"
            variant="primary"
            block
            disabled={status === 'sending' || (turnstileEnabled && !captcha)}
          >
            <Icon name="mail" size={18} />
            {status === 'sending' ? 'Sending…' : 'Email me a sign-in link'}
          </Button>
          <p className={styles.fine}>
            By joining, you agree to the <Link to="/terms">terms</Link> and{' '}
            <Link to="/guidelines">community guidelines</Link>. Read how your information is handled in the{' '}
            <Link to="/privacy">privacy policy</Link>.
          </p>
        </form>
      )}
    </Paper>
  );
}
